import { defineMessages } from "react-intl";

import Suspense from "@atlaskit/react-ufo/suspense";

import { LoadingSpinner } from "@/components/global-nav/user-profile-new/src/components/spinner";
import { ProductPlan } from "@/components/wpl/pricing/src/price-finder/constants.tsx";
import PriceFinderSection from "@/components/wpl/pricing/src/price-finder/price-finder";
import { PriceFinderContextProvider } from "@/components/wpl/pricing/src/price-finder/price-finder-context.tsx";
import { createPlanFeatureMessages } from "@/components/wpl/pricing/src/price-finder/utils/create-price-finder-features.tsx";
import { usePricingPlanComparison } from "@/components/wpl/pricing/src/pricing-content-context.tsx";
import { UserLocale } from "@/utils/useGeoCurrency.ts";

import { AppPricingData } from "../../../../../../../contentful/index.tsx";

export interface RovoPriceFinderProps {
    pricing?: AppPricingData;
    countryCode?: UserLocale;
}

export const RovoPriceFinder = (props: RovoPriceFinderProps) => {
    const content = {
        standard: usePricingPlanComparison("Rovo")
    };

    const planFeatureMessages = createPlanFeatureMessages(content, "standard");

    const rovoProductPlans: ProductPlan[] = [
        {
            planName: "Standard",
            offeringSku: "rovo-org-purchase-sku",
            recommendationLimit: 100000,
            signupLimit: 35000,
            signupURLInfo: {
                urlDomain: window.location.origin,
                urlPath: "/software/rovo/start",
                shouldLocalize: true
            },
            planFeatureMessages: planFeatureMessages
        }
    ];
    const messages = defineMessages({
        unitCountInputTextRedesign: {
            id: "RovoSoftwarePricingPage.priceFinder.unitCountInputTextRedesign",
            defaultMessage: "Team size:"
        },
        unitCountInputTerm: {
            id: "RovoSoftwarePricingPage.priceFinder.unitCountInputTerm",
            defaultMessage: "users"
        },
        cardOverSignupLimit: {
            id: "RovoSoftwarePricingPage.priceFinder.cardOverSignupLimit",
            defaultMessage: "Your user count exceeds this plan's limit"
        },
        cardEnterpriseMinimumCount: {
            id: "RovoSoftwarePricingPage.priceFinder.cardEnterpriseMinimumCount",
            defaultMessage:
                "Enterprise plan pricing is available when you enter {enterpriseMinimumCount} or more users above",
            description:
                "Enterprise is a plan name and should not be translated"
        },
        cardUnitCountTierRange: {
            id: "RovoSoftwarePricingPage.priceFinder.cardUnitCountTierRange",
            defaultMessage: "User tier: {lowerLimit} - {upperLimit}"
        },
        cardMonthlyPerUnitCount: {
            id: "RovoSoftwarePricingPageTeam24.priceFinder.cardMonthlyPerUnitCount",
            defaultMessage: "per user / month"
        }
    });

    return (
        <Suspense
            interactionName={"rovo-price-finder"}
            fallback={LoadingSpinner}
        >
            <PriceFinderContextProvider
                dataProductKey="rovo"
                productPlans={rovoProductPlans}
                defaultUnitCount={375}
            >
                <PriceFinderSection
                    messages={messages}
                    serverSideCCPPricing={props.pricing?.ccpPricingData?.rovo}
                    countryCode={props.countryCode}
                    {...props}
                />
            </PriceFinderContextProvider>
        </Suspense>
    );
};
RovoPriceFinder.displayName = "RovoPriceFinderSection";
